import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { getMecanique } from '../../utils/mecaniques';
import { formatMoney } from '../../utils/format';
import { GraduationCap, Lightbulb, CheckCircle2, Sigma } from 'lucide-react';

const fmtEffets = (e) => {
  if (!e) return null;
  const parts = [
    e.traficMult && e.traficMult !== 1 && `Trafic ×${e.traficMult}`,
    e.conversionMult && e.conversionMult !== 1 && `Conversion ×${e.conversionMult}`,
    e.surcoutPctCa && (e.surcoutPctCa < 0 ? `Marge +${Math.abs(e.surcoutPctCa)} % du CA` : `Coût ${e.surcoutPctCa} % du CA`),
    e.satisfactionDelta && `Satisfaction ${e.satisfactionDelta > 0 ? '+' : ''}${e.satisfactionDelta} pts`,
    e.stockBonusPct && `Stock +${e.stockBonusPct} %`,
    e.stockPertePct && `Stock −${e.stockPertePct} %`,
  ].filter(Boolean);
  const duree = e.duree_jours ? ` pendant ${e.duree_jours} j` : '';
  return parts.length ? parts.join(' · ') + duree : null;
};

/** Pop-up pédagogique « mécanique retail » : décision → explication du concept. */
export const MecaniqueRetailPopup = () => {
  const active = useGameStore(s => s.retail?.mecaniqueActive);
  const resoudreMecanique = useGameStore(s => s.resoudreMecanique);
  const treasury = useGameStore(s => s.treasury) || 0;
  // Phase pédagogie : conservée localement après résolution (le store a déjà purgé mecaniqueActive).
  const [resolu, setResolu] = useState(null); // { mecanique, choixIdx }

  const mecanique = resolu?.mecanique || (active ? getMecanique(active.id) : null);
  if (!mecanique) return null;
  const phase = resolu ? 'pedagogie' : 'decision';
  const choixFait = resolu ? mecanique.choix[resolu.choixIdx] : null;

  const choisir = (idx) => {
    const res = resoudreMecanique(idx);
    if (res?.ok) setResolu({ mecanique, choixIdx: idx });
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="mecanique-popup-overlay">
      <div className="bg-ink-800 border border-ink-600 rounded-xl w-full max-w-md max-h-[90vh] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 flex flex-col" data-testid="mecanique-popup">
        <div className="bg-accent/15 border-b border-accent/30 px-5 py-4 flex items-center gap-3 shrink-0">
          <span className="text-2xl">{mecanique.emoji}</span>
          <div className="min-w-0">
            <p className="text-accent text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
              <GraduationCap className="w-3 h-3" /> Mécanique retail — {mecanique.concept}
            </p>
            <h3 className="text-foreground font-bold text-base leading-tight">{mecanique.titre}</h3>
          </div>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          {phase === 'decision' && (
            <>
              <p className="text-slate-400 text-sm leading-relaxed">{mecanique.situation}</p>
              <div className="space-y-2 pt-1">
                {mecanique.choix.map((c, idx) => {
                  const cout = c.cout || 0;
                  const gain = c.gain || 0;
                  const effets = fmtEffets(c.effets);
                  return (
                    <button key={c.label} type="button" onClick={() => choisir(idx)}
                      className="w-full text-left border border-ink-600 rounded-lg p-3 hover:border-accent hover:bg-accent/5 transition-all group"
                      data-testid={`mecanique-choix-${idx}`}>
                      <div className="flex justify-between items-start gap-2">
                        <span className="font-medium text-slate-200 text-sm group-hover:text-foreground">{c.label}</span>
                        {cout > 0 && <span className="text-danger text-xs font-semibold whitespace-nowrap">-{formatMoney(cout)}</span>}
                        {gain > 0 && <span className="text-money text-xs font-semibold whitespace-nowrap">+{formatMoney(gain)}</span>}
                      </div>
                      <p className="text-slate-500 text-xs mt-1">{c.detail}</p>
                      {effets && <p className="text-[10px] text-emerald-400 mt-1">{effets}</p>}
                      {cout > 0 && cout > treasury && (
                        <p className="text-[10px] text-warn mt-1">⚠️ Trésorerie après : {formatMoney(treasury - cout)}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {phase === 'pedagogie' && (
            <div data-testid="mecanique-pedagogie" className="space-y-3">
              <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Votre choix</p>
                <p className="text-sm text-slate-200 font-medium">{choixFait.label}</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{choixFait.verdict}</p>
              </div>
              <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
                <p className="text-[10px] uppercase tracking-wider text-accent font-bold mb-1 flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" /> Le concept : {mecanique.concept}
                </p>
                <p className="text-xs text-slate-300 leading-relaxed">{mecanique.pedagogie.definition}</p>
                {mecanique.pedagogie.formule && (
                  <p className="mt-2 font-mono text-[11px] text-amber-300 bg-ink-950/50 rounded px-2 py-1.5 flex items-start gap-1.5">
                    <Sigma className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {mecanique.pedagogie.formule}
                  </p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">À retenir</p>
                <ul className="space-y-1.5">
                  {mecanique.pedagogie.retenir.map((pt) => (
                    <li key={pt} className="flex items-start gap-1.5 text-xs text-slate-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
              <button type="button" onClick={() => setResolu(null)}
                className="w-full btn-tech-primary text-sm" data-testid="mecanique-fermer">
                J'ai compris
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MecaniqueRetailPopup;
